<template>
  <div class="plugin-modal">
    <div v-if="plugin" class="content-layout">
      <h3 class="feature-name">{{ plugin.name }}</h3>

      <div class="description-preview-column">
        <p class="feature-description">
          You need to enable the <strong>{{ plugin.name }}</strong> plugin in
          order to access the <strong>{{ featureName }}</strong> feature.
        </p>
        <p class="feature-description">
          Open the Plugins settings to configure and enable the
          <strong>{{ plugin.name }}</strong> plugin.
        </p>

        <div class="feature-preview">
          <div v-if="isLoadingAsset" class="loading">Loading preview...</div>
          <img
            v-else-if="assetUrl"
            :src="assetUrl"
            alt="Plugin preview"
            class="preview-image"
          />
          <div v-else class="preview-error">Failed to load preview</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";

import {
  getAsset,
  getAssetUrl,
  type ContentfulAsset,
} from "@/utils/contentful";
import { getPlugin, type PluginId } from "@/utils/plugins";

const PLUGIN_IDS = [
  "menubar-widget",
  "dashboard",
  "contextual-capture",
] satisfies PluginId[];

const isPluginId = (id: string): id is PluginId =>
  PLUGIN_IDS.some((pluginId) => pluginId === id);

export default defineComponent({
  name: "PluginModal",
  props: {
    pluginId: {
      type: String,
      required: true,
    },
    featureName: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const plugin = isPluginId(props.pluginId)
      ? getPlugin(props.pluginId)
      : undefined;
    const isLoadingAsset = ref(false);
    const asset = ref<ContentfulAsset | null>(null);
    const assetUrl = ref<string | null>(null);

    const loadPreviewAsset = async () => {
      if (!plugin?.asset.large) return;
      isLoadingAsset.value = true;
      try {
        const fetchedAsset = await getAsset(plugin.asset.large);
        asset.value = fetchedAsset;
        assetUrl.value = getAssetUrl(fetchedAsset);
      } catch (error) {
        window.console.error("Failed to fetch download preview asset:", error);
        asset.value = null;
        assetUrl.value = null;
      } finally {
        isLoadingAsset.value = false;
      }
    };
    loadPreviewAsset();

    return { plugin, asset, assetUrl, isLoadingAsset };
  },
});
</script>

<style scoped lang="scss">
.plugin-modal {
  display: flex;
  flex-direction: column;
  max-width: 1000px;
  margin-top: 32px;
  margin-inline-start: 48px;
  margin-inline-end: 48px;

  .content-layout {
    display: flex;
    flex-direction: column;
    row-gap: 16px;
  }

  .feature-name {
    @extend %font-h2;
    margin: 0;
    @include themed() {
      color: t($primary);
    }
  }

  .description-preview-column {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .feature-description {
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    margin: 0;
    @include themed() {
      color: t($grey-60);
    }
  }

  .feature-preview {
    width: 100%;

    .preview-image {
      width: 100%;
      max-height: 600px;
      object-fit: contain;
      border-radius: $border-radius-l;
    }
  }
}
</style>
